import { useState, useEffect } from "react";
import { DadosRecusaProfissional } from "../../types";
import { TableContainer } from "../../styles";
import { formatarDataHora } from "../../utils";
import api from "../../../../../../../../../services/api";

interface BlocoRecusaProfissionalProps {
    dados: DadosRecusaProfissional;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    
    const valueString = String(value);
    if (valueString === '0' || valueString === 'null' || valueString === 'undefined') {
        return '—';
    }
    
    return valueString;
};

const formatarNomeProfissional = async (id_usuario: number | null | undefined, nomeDisponivel?: string): Promise<string> => {
    try {
        if (!id_usuario || id_usuario === 0) {
            return "—";
        }
        
        const id = Number(id_usuario);
        if (isNaN(id) || id <= 0) {
            return "—";
        }
        
        if (nomeDisponivel) {
            return `${nomeDisponivel} (D${id})`;
        }
        
        try {
            const response = await api.get(`usuario/${id}`);
            if (response?.data?.status === 'success' && response?.data?.result?.nome) {
                return `${response.data.result.nome} (D${id})`;
            }
        } catch (error: any) {
            if (error?.response?.status && error.response.status !== 404) {
                return `D${id}`;
            }
        }
        
        return `D${id}`;
    } catch {
        return `D${id_usuario}`;
    }
};

export default function BlocoRecusaProfissional({ dados }: BlocoRecusaProfissionalProps) {
    const [camposRecusa, setCamposRecusa] = useState<Array<{ campo: string; antes: string | null; depois: string }>>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const processarCampos = async () => {
            if (!dados) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const temDadosAntes = dados.dados_antes && typeof dados.dados_antes === 'object' && Object.keys(dados.dados_antes).length > 0;
                const campos: Array<{ campo: string; antes: string | null; depois: string }> = [];

                if (dados.justificativa) {
                    campos.push({
                        campo: "Justificativa",
                        antes: temDadosAntes && dados.dados_antes?.justificativa ? formatValue(dados.dados_antes.justificativa) : null,
                        depois: formatValue(dados.justificativa)
                    });
                }

                if (dados.id_usuario) {
                    const idUsuario = Number(dados.id_usuario);
                    if (!isNaN(idUsuario) && idUsuario > 0) {
                        let antesProfissional: string | null = null;
                        if (temDadosAntes && dados.dados_antes?.id_usuario) {
                            const idAntes = Number(dados.dados_antes.id_usuario);
                            if (!isNaN(idAntes) && idAntes > 0) {
                                antesProfissional = await formatarNomeProfissional(idAntes);
                            }
                        }

                        const depoisProfissional = await formatarNomeProfissional(idUsuario, dados.nome_profissional);

                        campos.push({
                            campo: "Profissional Vinculado",
                            antes: antesProfissional,
                            depois: depoisProfissional
                        });
                    }
                }

                if (dados.criado) {
                    let antesData: string | null = null;
                    if (temDadosAntes) {
                        const dataAntes = dados.dados_antes?.criado || dados.dados_antes?.modificado;
                        if (dataAntes) {
                            antesData = formatarDataHora(String(dataAntes));
                        }
                    }

                    campos.push({
                        campo: "Registrado em",
                        antes: antesData,
                        depois: formatarDataHora(String(dados.criado))
                    });
                }

                setCamposRecusa(campos);
            } catch (error) {
                setCamposRecusa([]);
            } finally {
                setLoading(false);
            }
        };

        processarCampos();
    }, [dados]);

    if (!dados || camposRecusa.length === 0) {
        return null;
    }

    const temDadosAntes = dados.dados_antes && typeof dados.dados_antes === 'object' && Object.keys(dados.dados_antes).length > 0;

    return (
        <div className="expanded-section">
            <h4 className="section-title">Recusa do profissional</h4>
            <TableContainer>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th className="field-col">Campo</th>
                            {temDadosAntes && (
                                <>
                                    <th className="before-col">Antes</th>
                                    <th className="after-col">Depois</th>
                                </>
                            )}
                            {!temDadosAntes && (
                                <th className="value-col">Valor</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {camposRecusa.map((campo, index) => (
                            <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                <td className="field-cell">{campo.campo}</td>
                                {temDadosAntes ? (
                                    <>
                                        <td className="before-cell">{campo.antes || '—'}</td>
                                        <td className="after-cell">
                                            <div className="after-content">
                                                <span className="highlight-bar" />
                                                <span>{campo.depois || '—'}</span>
                                            </div>
                                        </td>
                                    </>
                                            ) : (
                                                <td className={`value-cell ${campo.depois === '—' ? 'empty-value' : ''}`}>
                                                    {campo.depois || '—'}
                                                </td>
                                            )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </TableContainer>
        </div>
    );
}

